import React, { useMemo } from "react";
import { oneOf, string, bool, oneOfType, elementType } from "prop-types";

import { classes } from "../utils/classes";
import pureFC from "../utils/pureFC";
import fontWeight from "../utils/fontWeight";
import pureProps, { deepMergeProps } from "../utils/pureProps";
import { spacingClassName, spacingProps, spacingPropTypes } from "../props/spacing";
import { colorPropTypes, sxClassName, sxProps, sxPropTypes } from "../props/sx";
import { utilityClassName, utilityProps, utilityPropTypes } from "../props/utility";
import { sizingClassName, sizingProps, sizingPropTypes } from "../props/sizing";

const Link = ({
	Tag = "a",
	isBaseClassName = true,
	external = false,
	size,
	weight,
	color,
	hoverColor,
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	sizing = { ...sizingProps },
	className,
	...props
}) => {
	const sizeProps = size ? { fontSize: size } : {};
	const weightProps = weight ? { fontWeight: weight } : {};
	const defaultColorProps = color ? { fontColor: color } : { fontColor: null };
	const defaultColorHoverProps = hoverColor ? { fontColor: hoverColor } : { fontColor: null };
	const colorProps = { ...defaultColorProps, hover: { ...defaultColorHoverProps } };
	const classNameList = useMemo(
		() =>
			classes(
				isBaseClassName && "link",
				external && "link--external",
				...utilityClassName(utility),
				...sxClassName(
					deepMergeProps({ ...sizeProps, ...weightProps, ...colorProps }, pureProps(sx)),
				),
				...spacingClassName(spacing),
				...sizingClassName(sizing),
				className,
			),
		[sizeProps, weightProps, colorProps],
	);
	const classNameProp = classNameList ? { className: classNameList } : {};
	return <Tag {...props} {...classNameProp} />;
};

Link.propTypes = {
	Tag: oneOfType([oneOf(["a", "button"]), elementType]),
	isBaseClassName: bool,
	external: bool,
	size: oneOf(["xxxl", "xxl", "xl", "l", "m", "s", "xs"]),
	weight: oneOf(Object.keys(fontWeight)),
	color: colorPropTypes,
	hoverColor: colorPropTypes,
	utility: utilityPropTypes,
	sx: sxPropTypes,
	sizing: sizingPropTypes,
	spacing: spacingPropTypes,
	className: string,
};

const LinkButton = props => <Link Tag="button" type="button" {...props} />;

Link.Button = LinkButton;

export default pureFC(Link);
